import Shepherd from 'shepherd.js';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;
export type ShepherdStep = {
  id?: string;
  getTarget?: () => HTMLElement | null;
  target?: HTMLElement | null;
};
type ShepherdStepWithInternals = ShepherdStep & {
  cleanup?: () => void;
  update?: () => void;
  hide?: () => void;
  show?: () => Promise<unknown>;
  el?: HTMLElement | null;
  target?: HTMLElement | null;
  options?: Record<string, unknown>;
  isOpen?: () => boolean;
};

/** Shepherd step is current and its tooltip is visible (not dismissed via OK / hide). */
export function isShepherdStepOpen(step: ShepherdStep | undefined): boolean {
  if (!step) {
    return false;
  }
  const withOpen = step as ShepherdStepWithInternals;
  return withOpen.isOpen?.() ?? false;
}

/** Current step matches id and user has not dismissed it with OK (tooltip may still be opening). */
export function isShepherdTourStepSpotlightEligible(tour: ShepherdTour, stepId: string): boolean {
  if (typeof Shepherd === 'undefined' || Shepherd.activeTour !== tour) {
    return false;
  }
  const step = tour.getCurrentStep?.() as ShepherdStep | undefined;
  if (!step || step.id !== stepId) {
    return false;
  }
  return !wasShepherdStepDismissedByUser(step);
}

/** Current step matches id, tooltip open, and user has not dismissed it with OK. */
export function isShepherdTourStepInteractive(tour: ShepherdTour, stepId: string): boolean {
  if (typeof Shepherd === 'undefined' || Shepherd.activeTour !== tour) {
    return false;
  }
  const step = tour.getCurrentStep?.() as ShepherdStep | undefined;
  if (!step || step.id !== stepId) {
    return false;
  }
  return isShepherdStepOpen(step) && !wasShepherdStepDismissedByUser(step);
}

/** Use on steps with inputs, search, or autocomplete — avoids mobile focus/keyboard loss. */
export const SHEPHERD_INTERACTIVE_STEP_OPTIONS = {
  scrollTo: false as const,
  canClickTarget: true,
  floatingUIOptions: {
    strategy: 'fixed' as const,
  },
};

const BODY_TYPING_FROZEN_CLASS = 'shepherd-location-typing-frozen';
const TOOLTIP_FROZEN_ATTR = 'data-shepherd-position-frozen';

const originalStepUpdate = new WeakMap<ShepherdStepWithInternals, () => void>();
const originalStepHide = new WeakMap<ShepherdStepWithInternals, () => void>();

const SHOW_PATCHED = '__shepherdShowPatched';

export const SHEPHERD_GUIDED_TOUR_BODY_CLASS = 'shepherd-guided-tour-active';

/** Blocks clicks on dimmed areas while a step tooltip is open (removed on OK-dismiss / tour end). */
export const SHEPHERD_TOUR_CLICK_SHIELD_CLASS = 'shepherd-tour-click-shield-active';

let notifyShepherdTourUiChanged: (() => void) | undefined;

/** Run inside Angular zone after tip open/close so host bindings (e.g. back buttons) update. */
export function setShepherdTourUiChangedNotifier(notify: () => void): void {
  notifyShepherdTourUiChanged = notify;
}

function syncShepherdTourClickShield(tour: ShepherdTour): void {
  const step = tour.getCurrentStep?.() as ShepherdStepWithInternals | undefined;
  const shieldOn =
    !!step && isShepherdStepOpen(step) && !wasShepherdStepDismissedByUser(step);
  document.body.classList.toggle(SHEPHERD_TOUR_CLICK_SHIELD_CLASS, shieldOn);
  notifyShepherdTourUiChanged?.();
}

let shepherdTourInternalUpdate = false;

/** Step `tour.show()` is navigating to — allows hide only when leaving the current step. */
let tourTransitionTargetStep: ShepherdStepWithInternals | null = null;

/** Steps the user dismissed with OK — do not auto re-show until that step is shown again. */
const userDismissedSteps = new WeakSet<ShepherdStepWithInternals>();

/** Steps temporarily excluded from tooltip persistence (e.g. tour Back mid-transition). */
const suppressTooltipPersistenceSteps = new WeakSet<ShepherdStepWithInternals>();

let locationTypingLocked = false;
let frozenAutocompleteStep: ShepherdStepWithInternals | undefined;
let stopAutoUpdateIntervalId: ReturnType<typeof setInterval> | null = null;
let detachTypingFreezeListeners: (() => void) | null = null;
let detachWindowFreezeListeners: (() => void) | null = null;
let modalOverlayRafPaused = false;

type ShepherdModalInstance = {
  hide?: () => void;
  setupForStep?: (step: ShepherdStepWithInternals & { target?: HTMLElement | null; options?: Record<string, unknown> }) => void;
  positionModal?: (
    padding?: number,
    radius?: number,
    xOffset?: number,
    yOffset?: number,
    scrollParent?: HTMLElement | null,
    targetElement?: HTMLElement | null,
  ) => void;
};

function getActiveShepherdTour(): ShepherdTour | null {
  if (typeof Shepherd === 'undefined') {
    return null;
  }
  return Shepherd.activeTour === undefined ? null : (Shepherd.activeTour as ShepherdTour);
}

function getScrollParentForOverlay(element: HTMLElement | null): HTMLElement | null {
  if (!element) {
    return null;
  }
  const overflowY = window.getComputedStyle(element).overflowY;
  const isScrollable = overflowY !== 'hidden' && overflowY !== 'visible';
  if (isScrollable && element.scrollHeight >= element.clientHeight) {
    return element;
  }
  return element.parentElement ? getScrollParentForOverlay(element.parentElement) : null;
}

/** Stop Shepherd modal SVG cutout RAF loop; keep the last cutout visible only if overlay was already shown. */
function pauseShepherdModalOverlayRaf(step: ShepherdStepWithInternals | undefined): void {
  const tour = getActiveShepherdTour();
  const modal = (tour as { modal?: ShepherdModalInstance } | null)?.modal;
  const target = step?.target ?? getStepTarget(step);
  const container = document.querySelector('.shepherd-modal-overlay-container');
  const overlayWasVisible = container?.classList.contains('shepherd-modal-is-visible') ?? false;
  if (!modal?.positionModal || !target || !overlayWasVisible) {
    return;
  }
  const opts = step?.options ?? {};
  const padding = (opts['modalOverlayOpeningPadding'] as number | undefined) ?? 10;
  const radius = opts['modalOverlayOpeningRadius'] as number | undefined;
  const xOffset = (opts['modalOverlayOpeningXOffset'] as number | undefined) ?? 0;
  const yOffset = (opts['modalOverlayOpeningYOffset'] as number | undefined) ?? 0;
  modal.positionModal(padding, radius, xOffset, yOffset, getScrollParentForOverlay(target), target);
  modal.hide?.();
  modalOverlayRafPaused = true;
  container?.classList.add('shepherd-modal-is-visible');
}

/** Hide dim overlay after OK / step.hide — prevents solid black screen when focusing inputs. */
export function ensureShepherdModalOverlayHidden(): void {
  modalOverlayRafPaused = false;
  document.querySelector('.shepherd-modal-overlay-container')?.classList.remove('shepherd-modal-is-visible');
  getActiveShepherdTour()?.modal?.hide?.();
}

/** Show SVG dim cutout around the step target (training results / book / empty steps). */
export function ensureShepherdModalOverlayVisibleForStep(
  step: ShepherdStepWithInternals | undefined,
): void {
  if (!step) {
    return;
  }
  modalOverlayRafPaused = false;
  const tour = getActiveShepherdTour();
  const modal = (tour as { modal?: ShepherdModalInstance } | null)?.modal;
  if (!modal?.setupForStep) {
    return;
  }
  modal.setupForStep(
    step as ShepherdStepWithInternals & {
      target?: HTMLElement | null;
      options?: Record<string, unknown>;
    },
  );
  document.querySelector('.shepherd-modal-overlay-container')?.classList.add('shepherd-modal-is-visible');
}

function resumeShepherdModalOverlayRaf(step: ShepherdStepWithInternals | undefined): void {
  if (!modalOverlayRafPaused) {
    return;
  }
  modalOverlayRafPaused = false;
  const tour = getActiveShepherdTour();
  const modal = (tour as { modal?: ShepherdModalInstance } | null)?.modal;
  if (!modal?.setupForStep || !step) {
    return;
  }
  modal.setupForStep(step as ShepherdStepWithInternals & { target?: HTMLElement | null; options?: Record<string, unknown> });
}

function getStepTarget(step: ShepherdStep | undefined): HTMLElement | null {
  if (!step) {
    return null;
  }
  return step.getTarget?.() ?? step.target ?? null;
}

/** True while a tour location/autocomplete field has focus — blocks Shepherd reposition. */
export function isShepherdLocationTypingLocked(): boolean {
  return locationTypingLocked;
}

function getActiveShepherdTooltip(): HTMLElement | null {
  return document.querySelector('.shepherd-element.shepherd-enabled');
}

function freezeShepherdTooltipPosition(): void {
  const el = getActiveShepherdTooltip();
  if (!el || el.getAttribute(TOOLTIP_FROZEN_ATTR) === '1') {
    return;
  }
  const rect = el.getBoundingClientRect();
  el.setAttribute(TOOLTIP_FROZEN_ATTR, '1');
  el.style.setProperty('position', 'fixed', 'important');
  el.style.setProperty('top', `${rect.top}px`, 'important');
  el.style.setProperty('left', `${rect.left}px`, 'important');
  el.style.setProperty('margin', '0', 'important');
  el.style.setProperty('transform', 'none', 'important');
  el.style.setProperty('transition', 'none', 'important');
  document.body.classList.add(BODY_TYPING_FROZEN_CLASS);
}

function unfreezeShepherdTooltipPosition(): void {
  document.body.classList.remove(BODY_TYPING_FROZEN_CLASS);
  document.querySelectorAll(`.shepherd-element[${TOOLTIP_FROZEN_ATTR}="1"]`).forEach((node) => {
    const el = node as HTMLElement;
    el.removeAttribute(TOOLTIP_FROZEN_ATTR);
    el.style.removeProperty('position');
    el.style.removeProperty('top');
    el.style.removeProperty('left');
    el.style.removeProperty('margin');
    el.style.removeProperty('transform');
    el.style.removeProperty('transition');
  });
}

function stopFloatingUiAutoUpdate(step: ShepherdStepWithInternals | undefined): void {
  step?.cleanup?.();
  if (step) {
    step.cleanup = undefined;
  }
}

function startAutoUpdateSuppressionLoop(step: ShepherdStepWithInternals | undefined): void {
  if (stopAutoUpdateIntervalId !== null) {
    return;
  }
  stopFloatingUiAutoUpdate(step);
  stopAutoUpdateIntervalId = setInterval(() => {
    if (!locationTypingLocked) {
      return;
    }
    stopFloatingUiAutoUpdate(frozenAutocompleteStep);
  }, 80);
}

function stopAutoUpdateSuppressionLoop(): void {
  if (stopAutoUpdateIntervalId !== null) {
    clearInterval(stopAutoUpdateIntervalId);
    stopAutoUpdateIntervalId = null;
  }
}

function patchStepUpdateWhileTyping(
  step: ShepherdStepWithInternals | undefined,
  resolveTourRoot: () => HTMLElement | null,
): void {
  if (!step) {
    return;
  }
  const original = step.update?.bind(step);
  if (!original) {
    return;
  }
  if (!originalStepUpdate.has(step)) {
    originalStepUpdate.set(step, original);
  }
  step.update = () => {
    if (
      locationTypingLocked ||
      isEditableFocusInsideElement(resolveTourRoot())
    ) {
      return;
    }
    originalStepUpdate.get(step)?.();
  };
}

function attachWindowFreezeListeners(): void {
  if (detachWindowFreezeListeners) {
    return;
  }
  const blockWhileTyping = (): void => {
    if (!locationTypingLocked) {
      return;
    }
    stopFloatingUiAutoUpdate(frozenAutocompleteStep);
    freezeShepherdTooltipPosition();
    if (!modalOverlayRafPaused) {
      pauseShepherdModalOverlayRaf(frozenAutocompleteStep);
    }
  };
  window.addEventListener('resize', blockWhileTyping, true);
  window.addEventListener('scroll', blockWhileTyping, true);
  detachWindowFreezeListeners = () => {
    window.removeEventListener('resize', blockWhileTyping, true);
    window.removeEventListener('scroll', blockWhileTyping, true);
    detachWindowFreezeListeners = null;
  };
}

function detachWindowFreezeListenersIfAny(): void {
  detachWindowFreezeListeners?.();
}

export function enterShepherdLocationTypingFreeze(
  step: ShepherdStepWithInternals | undefined,
  resolveTourRoot: () => HTMLElement | null,
): void {
  locationTypingLocked = true;
  frozenAutocompleteStep = step ?? frozenAutocompleteStep;
  patchStepUpdateWhileTyping(step ?? frozenAutocompleteStep, resolveTourRoot);
  stopFloatingUiAutoUpdate(step ?? frozenAutocompleteStep);
  startAutoUpdateSuppressionLoop(step ?? frozenAutocompleteStep);
  attachWindowFreezeListeners();
  queueMicrotask(() => {
    if (!locationTypingLocked) {
      return;
    }
    freezeShepherdTooltipPosition();
    stopFloatingUiAutoUpdate(frozenAutocompleteStep);
    pauseShepherdModalOverlayRaf(step ?? frozenAutocompleteStep);
  });
}

export function exitShepherdLocationTypingFreeze(
  resolveTourRoot?: () => HTMLElement | null,
): void {
  if (!locationTypingLocked) {
    return;
  }
  locationTypingLocked = false;
  stopAutoUpdateSuppressionLoop();
  detachWindowFreezeListenersIfAny();
  unfreezeShepherdTooltipPosition();

  const step = frozenAutocompleteStep;
  frozenAutocompleteStep = undefined;

  if (isShepherdStepOpen(step)) {
    resumeShepherdModalOverlayRaf(step);
  } else {
    ensureShepherdModalOverlayHidden();
  }

  if (!step || !resolveTourRoot) {
    return;
  }
  if (isEditableFocusInsideElement(resolveTourRoot())) {
    return;
  }
}

function attachLocationTypingFreezeToTourRoot(
  resolveTourRoot: () => HTMLElement | null,
  isStepActive: () => boolean,
  getAutocompleteStep: () => ShepherdStepWithInternals | undefined,
): () => void {
  const lockTypingIfTourInput = (target: EventTarget | null): void => {
    if (!isStepActive()) {
      return;
    }
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
      return;
    }
    const root = resolveTourRoot();
    if (!root?.contains(target)) {
      return;
    }
    enterShepherdLocationTypingFreeze(getAutocompleteStep(), resolveTourRoot);
  };

  const onPointerDown = (ev: Event): void => {
    lockTypingIfTourInput(ev.target);
  };

  const onFocus = (ev: Event): void => {
    lockTypingIfTourInput(ev.target);
  };

  const onBlur = (ev: Event): void => {
    if (!isStepActive()) {
      return;
    }
    const target = ev.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
      return;
    }
    const root = resolveTourRoot();
    if (!root?.contains(target)) {
      return;
    }
    const focusEvent = ev as FocusEvent;
    if (isSpuriousAutocompleteTourBlur(focusEvent, root)) {
      scheduleTourEditableRefocus(target, root);
      return;
    }
    window.setTimeout(() => {
      if (!isStepActive()) {
        exitShepherdLocationTypingFreeze(resolveTourRoot);
        return;
      }
      const active = document.activeElement;
      if (active?.closest(AUTOCOMPLETE_TOUR_OVERLAY_SELECTOR)) {
        return;
      }
      if (isEditableFocusInsideElement(root)) {
        return;
      }
      exitShepherdLocationTypingFreeze(resolveTourRoot);
    }, 200);
  };

  const onInputWhileLocked = (): void => {
    if (!locationTypingLocked || !isStepActive()) {
      return;
    }
    stopFloatingUiAutoUpdate(frozenAutocompleteStep);
    freezeShepherdTooltipPosition();
  };

  const bindRoot = (): void => {
    const root = resolveTourRoot();
    if (!root) {
      return;
    }
    root.addEventListener('pointerdown', onPointerDown, true);
    root.addEventListener('touchstart', onPointerDown, true);
    root.addEventListener('focusin', onFocus, true);
    root.addEventListener('focusout', onBlur, true);
    root.addEventListener('input', onInputWhileLocked, true);
    root.addEventListener('keydown', onInputWhileLocked, true);
  };

  bindRoot();
  const rebindTimer = window.setTimeout(bindRoot, 400);

  return () => {
    window.clearTimeout(rebindTimer);
    const root = resolveTourRoot();
    if (!root) {
      return;
    }
    root.removeEventListener('pointerdown', onPointerDown, true);
    root.removeEventListener('touchstart', onPointerDown, true);
    root.removeEventListener('focusin', onFocus, true);
    root.removeEventListener('focusout', onBlur, true);
    root.removeEventListener('input', onInputWhileLocked, true);
    root.removeEventListener('keydown', onInputWhileLocked, true);
  };
}

function resolveTourStepByKey(
  tour: ShepherdTour,
  key?: number | string,
): ShepherdStepWithInternals | undefined {
  if (key === undefined) {
    return undefined;
  }
  if (typeof key === 'string') {
    return tour.getById?.(key) as ShepherdStepWithInternals | undefined;
  }
  const steps = (tour as unknown as { steps?: ShepherdStepWithInternals[] }).steps ?? [];
  return steps[key];
}

function patchStepHideGuard(step: ShepherdStepWithInternals): void {
  const stepAny = step as ShepherdStepWithInternals & { hide?: () => void };
  const original = stepAny.hide?.bind(stepAny);
  if (!original || originalStepHide.has(step)) {
    return;
  }
  originalStepHide.set(step, original);
  stepAny.hide = () => {
    if (userDismissedSteps.has(step)) {
      original();
      return;
    }
    const transitionTarget = tourTransitionTargetStep;
    if (shepherdTourInternalUpdate && transitionTarget && transitionTarget.id !== step.id) {
      original();
      return;
    }
    if (step.el) {
      step.el.hidden = false;
    }
  };
}

/**
 * Close only the current step tooltip (OK). Tour stays active; later steps can still show.
 */
export function dismissCurrentShepherdStep(tour: ShepherdTour): void {
  const step = tour.getCurrentStep?.() as ShepherdStepWithInternals | undefined;
  if (!step) {
    return;
  }
  userDismissedSteps.add(step);
  const original = originalStepHide.get(step);
  if (original) {
    original();
  } else {
    step.hide?.();
  }
  syncShepherdTourClickShield(tour);
  queueMicrotask(() => syncShepherdTourClickShield(tour));
}

export function wasShepherdStepDismissedByUser(step: ShepherdStep | undefined): boolean {
  if (!step) {
    return false;
  }
  return userDismissedSteps.has(step as ShepherdStepWithInternals);
}

/** Mark tour steps dismissed so tips do not re-open (e.g. Skip on multi-step modal tours). */
export function markShepherdTourStepsDismissedByUser(
  tour: ShepherdTour,
  stepIds: string[],
): void {
  for (const stepId of stepIds) {
    const step = tour.getById?.(stepId) as ShepherdStepWithInternals | undefined;
    if (step) {
      userDismissedSteps.add(step);
    }
  }
}

/** Allow a dismissed step to show again (e.g. tour Back from the next step). */
export function clearShepherdStepDismissedByUser(step: ShepherdStep | undefined): void {
  if (!step) {
    return;
  }
  userDismissedSteps.delete(step as ShepherdStepWithInternals);
}

export function suppressShepherdStepTooltipPersistence(step: ShepherdStep | undefined): void {
  if (!step) {
    return;
  }
  suppressTooltipPersistenceSteps.add(step as ShepherdStepWithInternals);
}

export function clearShepherdStepTooltipPersistenceSuppress(step: ShepherdStep | undefined): void {
  if (!step) {
    return;
  }
  suppressTooltipPersistenceSteps.delete(step as ShepherdStepWithInternals);
}

/**
 * Hide the current step tooltip for tour.back() / form Back without OK-dismiss.
 * Suppresses tooltip persistence re-open while `targetStepId` is shown next.
 */
export function hideCurrentShepherdStepForTourTransition(
  tour: ShepherdTour,
  targetStepId: string,
): void {
  const current = tour.getCurrentStep?.() as ShepherdStepWithInternals | undefined;
  const targetStep = tour.getById?.(targetStepId) as ShepherdStepWithInternals | undefined;
  if (!current || !targetStep || current.id === targetStep.id) {
    return;
  }
  tourTransitionTargetStep = targetStep;
  shepherdTourInternalUpdate = true;
  try {
    const original = originalStepHide.get(current);
    if (original) {
      original();
    } else {
      current.hide?.();
    }
  } finally {
    shepherdTourInternalUpdate = false;
    tourTransitionTargetStep = null;
  }
  syncShepherdTourClickShield(tour);
  queueMicrotask(() => syncShepherdTourClickShield(tour));
}

function patchTourShowGuard(tour: ShepherdTour): void {
  const tourAny = tour as ShepherdTour & {
    show?: (key?: number | string, forward?: boolean) => unknown;
  } & Record<string, unknown>;
  if (tourAny[SHOW_PATCHED]) {
    return;
  }
  tourAny[SHOW_PATCHED] = true;
  const originalShow = tourAny.show?.bind(tour);
  if (!originalShow) {
    return;
  }
  tourAny.show = (key?: number | string, forward?: boolean) => {
    const targetStep = resolveTourStepByKey(tour, key);
    const current = tour.getCurrentStep?.() as ShepherdStepWithInternals | undefined;
    if (
      targetStep &&
      current &&
      targetStep.id === current.id &&
      current.isOpen?.()
    ) {
      return Promise.resolve();
    }
    if (targetStep && wasShepherdStepDismissedByUser(targetStep)) {
      return Promise.resolve();
    }
    tourTransitionTargetStep = targetStep ?? null;
    shepherdTourInternalUpdate = true;
    try {
      return originalShow(key, forward);
    } finally {
      shepherdTourInternalUpdate = false;
      tourTransitionTargetStep = null;
    }
  };
}

function patchTourHideGuard(tour: ShepherdTour): void {
  const tourAny = tour as ShepherdTour & { hide?: () => void };
  const originalHide = tourAny.hide?.bind(tour);
  if (!originalHide) {
    return;
  }
  tourAny.hide = () => {
    const step = tour.getCurrentStep?.() as ShepherdStepWithInternals | undefined;
    if (step && !userDismissedSteps.has(step)) {
      return;
    }
    return originalHide();
  };
}

/** Re-open the current step tooltip after outside clicks / layout changes that hid it. */
function registerTourTooltipPersistenceGuard(tour: ShepherdTour): void {
  const ensureCurrentStepTooltipVisible = (): void => {
    if (shepherdTourInternalUpdate) {
      return;
    }
    if (typeof Shepherd === 'undefined' || Shepherd.activeTour !== tour) {
      return;
    }
    const step = tour.getCurrentStep?.() as ShepherdStepWithInternals | undefined;
    if (!step || userDismissedSteps.has(step) || suppressTooltipPersistenceSteps.has(step)) {
      return;
    }
    if (!step.isOpen?.()) {
      void step.show?.();
      return;
    }
    if (step.el) {
      step.el.hidden = false;
    }
  };

  const scheduleCheck = (): void => {
    queueMicrotask(ensureCurrentStepTooltipVisible);
    requestAnimationFrame(ensureCurrentStepTooltipVisible);
  };

  let listenersAttached = false;
  const attach = (): void => {
    if (listenersAttached) {
      return;
    }
    listenersAttached = true;
    document.addEventListener('pointerup', scheduleCheck, true);
    document.addEventListener('click', scheduleCheck, true);
  };
  const detach = (): void => {
    if (!listenersAttached) {
      return;
    }
    listenersAttached = false;
    document.removeEventListener('pointerup', scheduleCheck, true);
    document.removeEventListener('click', scheduleCheck, true);
  };

  tour.on('start', attach);
  tour.on('complete', detach);
  tour.on('cancel', detach);
}

function patchAllTourStepHides(tour: ShepherdTour): void {
  const steps = (tour as unknown as { steps?: ShepherdStepWithInternals[] }).steps ?? [];
  steps.forEach((step) => patchStepHideGuard(step));
}

/**
 * Keep the tour tooltip visible until Skip or the header close (X) — block accidental step.hide().
 */
export function registerTourDismissProtection(tour: ShepherdTour): void {
  patchTourShowGuard(tour);
  patchTourHideGuard(tour);
  patchAllTourStepHides(tour);
  registerTourTooltipPersistenceGuard(tour);

  tour.on('start', () => {
    document.body.classList.add(SHEPHERD_GUIDED_TOUR_BODY_CLASS);
    patchAllTourStepHides(tour);
    queueMicrotask(() => syncShepherdTourClickShield(tour));
    requestAnimationFrame(() => syncShepherdTourClickShield(tour));
  });

  tour.on('show', (ev: { step?: ShepherdStepWithInternals; previous?: ShepherdStep }) => {
    const step = ev?.step;
    const prev = ev?.previous as ShepherdStepWithInternals | undefined;
    if (step && step.id !== prev?.id) {
      userDismissedSteps.delete(step);
    }
    syncShepherdTourClickShield(tour);
    queueMicrotask(() => syncShepherdTourClickShield(tour));
  });

  tour.on('hide', (ev: { step?: ShepherdStepWithInternals }) => {
    queueMicrotask(() => syncShepherdTourClickShield(tour));
    const step = ev?.step;
    if (
      !step ||
      shepherdTourInternalUpdate ||
      userDismissedSteps.has(step) ||
      suppressTooltipPersistenceSteps.has(step)
    ) {
      return;
    }
    queueMicrotask(() => {
      if (typeof Shepherd === 'undefined' || Shepherd.activeTour !== tour) {
        return;
      }
      const current = tour.getCurrentStep?.() as ShepherdStepWithInternals | undefined;
      if (current !== step) {
        return;
      }
      if (suppressTooltipPersistenceSteps.has(step)) {
        return;
      }
      if (step.isOpen?.()) {
        return;
      }
      void (step as ShepherdStepWithInternals & { show?: () => Promise<unknown> }).show?.();
    });
  });

  const clearGuidedTourBodyClasses = (): void => {
    document.body.classList.remove(SHEPHERD_GUIDED_TOUR_BODY_CLASS);
    document.body.classList.remove(SHEPHERD_TOUR_CLICK_SHIELD_CLASS);
    ensureShepherdModalOverlayHidden();
  };
  tour.on('complete', clearGuidedTourBodyClasses);
  tour.on('cancel', clearGuidedTourBodyClasses);
}

/**
 * Guards for tours where users must type or pick from a dropdown while the tip stays open.
 * - Disables scroll-into-view side effects that blur inputs on mobile.
 * - Keeps pointer events enabled on fields inside the highlighted target.
 */
export function registerInteractiveTourGuards(tour: ShepherdTour): void {
  tour.on('show', (ev: { step?: ShepherdStep }) => {
    const target = getStepTarget(ev?.step);
    if (!target) {
      return;
    }
    target.style.pointerEvents = 'auto';
    target.querySelectorAll('input, textarea, select, button, label, [role="button"]').forEach((node) => {
      (node as HTMLElement).style.pointerEvents = 'auto';
    });
  });
}

/** Skip `tour.show(sameStep)` while the user is typing in the highlighted target. */
export function shouldSkipShepherdStepReshow(tour: ShepherdTour, stepId: string): boolean {
  const current = tour.getCurrentStep?.() as ShepherdStep | undefined;
  if (!current || current.id !== stepId) {
    return false;
  }
  if (locationTypingLocked) {
    return true;
  }
  const target = getStepTarget(current);
  return isEditableFocusInsideElement(target);
}

const AUTOCOMPLETE_TOUR_OVERLAY_SELECTOR =
  '.city-dropdown, .suggestions-dropdown, .location-suggestions, .tee-time-poll-location-dropdown, .connections-search-list, .cdk-overlay-container, .mat-datepicker-popup';

/** True when focus is on an input/textarea inside the tour highlight root (mobile typing). */
export function isEditableFocusInsideElement(root: HTMLElement | null): boolean {
  if (!root) {
    return false;
  }
  const active = document.activeElement;
  if (!(active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement)) {
    return false;
  }
  return root.contains(active);
}

/**
 * iOS/Android (and mobile DevTools emulation) often fire blur with null relatedTarget while the keyboard opens.
 * Skip Shepherd overlay refresh so step.update() does not dismiss the keyboard.
 */
export function isSpuriousAutocompleteTourBlur(event: FocusEvent, tourRoot: HTMLElement | null): boolean {
  if (!tourRoot) {
    return false;
  }
  const related = event.relatedTarget as HTMLElement | null;
  if (related?.closest(AUTOCOMPLETE_TOUR_OVERLAY_SELECTOR)) {
    return false;
  }
  if (related && tourRoot.contains(related)) {
    return false;
  }
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
    return false;
  }
  if (!tourRoot.contains(target)) {
    return false;
  }
  // Ghost blur: focus never left the field (common when the mobile keyboard opens).
  return document.activeElement === target;
}

/** Re-focus tour input after ghost blur (keyboard open / overlay layout). */
export function scheduleTourEditableRefocus(
  input: HTMLInputElement | HTMLTextAreaElement | null | undefined,
  tourRoot: HTMLElement | null,
): void {
  if (!input || !tourRoot?.contains(input)) {
    return;
  }
  const el = input;
  const refocus = (): void => {
    if (!tourRoot.contains(el)) {
      return;
    }
    const active = document.activeElement;
    if (active === el) {
      return;
    }
    if (
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement
    ) {
      if (tourRoot.contains(active)) {
        return;
      }
    }
    try {
      el.focus({ preventScroll: true });
    } catch {
      // ignore — element may be detached
    }
  };
  requestAnimationFrame(refocus);
  for (const ms of [50, 150, 300]) {
    window.setTimeout(refocus, ms);
  }
}

export function isTouchMobileDevice(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia('(max-width: 1024px)').matches || 'ontouchstart' in window;
}

/**
 * Shepherd modal RAF + Floating UI autoUpdate reposition on keyboard open and dismiss iOS/Android keyboards.
 * Disable modal overlay at tour level and call this for autocomplete steps.
 */
export function registerMobileAutocompleteTourGuards(
  tour: ShepherdTour,
  autocompleteStepIds: string[],
  resolveTourRoot: () => HTMLElement | null,
): void {
  const isAutocompleteStep = (stepId: string | undefined): boolean =>
    !!stepId && autocompleteStepIds.includes(stepId);

  const isTourOnAutocompleteStep = (): boolean => {
    if (typeof Shepherd === 'undefined' || Shepherd.activeTour !== tour) {
      return false;
    }
    const current = tour.getCurrentStep?.() as ShepherdStep | undefined;
    const stepId = current?.id;
    return !!stepId && isAutocompleteStep(stepId) && isShepherdTourStepInteractive(tour, stepId);
  };

  const getAutocompleteStep = (): ShepherdStepWithInternals | undefined =>
    tour.getCurrentStep?.() as ShepherdStepWithInternals | undefined;

  tour.on('show', (ev: { step?: ShepherdStepWithInternals }) => {
    const step = ev?.step;
    if (!isAutocompleteStep(step?.id)) {
      detachTypingFreezeListeners?.();
      detachTypingFreezeListeners = null;
      exitShepherdLocationTypingFreeze(resolveTourRoot);
      return;
    }

    detachTypingFreezeListeners?.();
    detachTypingFreezeListeners = attachLocationTypingFreezeToTourRoot(
      resolveTourRoot,
      isTourOnAutocompleteStep,
      getAutocompleteStep,
    );

    queueMicrotask(() => {
      patchStepUpdateWhileTyping(step, resolveTourRoot);
      stopFloatingUiAutoUpdate(step);
    });
  });

  const teardown = (): void => {
    detachTypingFreezeListeners?.();
    detachTypingFreezeListeners = null;
    exitShepherdLocationTypingFreeze(resolveTourRoot);
    stopAutoUpdateSuppressionLoop();
    detachWindowFreezeListenersIfAny();
    modalOverlayRafPaused = false;
  };
  tour.on('hide', (ev: { step?: ShepherdStep }) => {
    if (isAutocompleteStep(ev?.step?.id) && shepherdTourInternalUpdate) {
      exitShepherdLocationTypingFreeze(resolveTourRoot);
      ensureShepherdModalOverlayHidden();
    }
  });
  tour.on('complete', teardown);
  tour.on('cancel', teardown);
}
